// Certificado de conclusão de trilha em PDF (A4 paisagem).
//
// PRIVACIDADE — o que entra aqui é diferente do card de conquista: o card sai
// do dispositivo para uma rede social (por isso só leva os números da pessoa e
// o wordmark do produto), enquanto o certificado é documento INTERNO de
// capacitação, que a pessoa arquiva ou entrega à chefia. O nome da pessoa só
// é impresso se o contexto de auth já o tiver; sem ele, o certificado sai com
// uma linha em branco para preenchimento à mão. Nada é inventado nem inferido
// do e-mail.
//
// FONTES: só as embutidas do jsPDF (helvetica). Fonte externa exigiria embutir
// TTF no bundle e, pior, poderia falhar offline. As embutidas são codificadas
// em WinAnsi, que cobre o português inteiro (ç, ã, é, í, õ, ú) — há teste
// dedicado a isso em certificado.test.ts, porque acento quebrado num
// certificado é defeito visível.
import type {jsPDF} from 'jspdf'
import wordmarkUrl from '../../assets/licito-logo-sem-fundo.png'
import {getCurrentColors} from '../../utils/colors'

const COLORS = getCurrentColors()

// A4 paisagem em mm.
const LARGURA = 297
const ALTURA = 210

// Wordmark do PRODUTO (licito.guru), não `getCurrentImages()`. O brasão do
// órgão seria aceitável neste documento, mas os temas white-label servem SVG,
// que o jsPDF não embute sem canvg — e puxar essa dependência para o bundle da
// trilha não se paga. Proporção medida do arquivo (228x70).
const PROPORCAO_WORDMARK = 228 / 70

const COR_TEXTO = '#1B2430'
const COR_APAGADA = '#6E7884'
const COR_LINHA = '#C8D0DA'

export interface DadosCertificado {
    // Nome da trilha, como aparece na aba do mapa.
    trilha: string
    // Pílulas dominadas NESTA trilha (ver `dominadasDaTrilha`).
    dominadas: number
    // Nome de quem concluiu. Vazio = não disponível no contexto de auth: o
    // certificado sai com linha para preenchimento manual.
    nome: string
    emitidoEm?: Date
}

export function nomeDoArquivo(trilha: string): string {
    const base = trilha
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase()
    return `certificado-${base || 'trilha'}.pdf`
}

// Data de emissão por extenso, no fuso de Brasília — o mesmo dia "oficial" que
// o resto do módulo usa (ver `dataLocal` no domínio).
export function dataDeEmissao(quando: Date): string {
    return new Intl.DateTimeFormat('pt-BR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'America/Sao_Paulo',
    }).format(quando)
}

export function frasePilulas(dominadas: number): string {
    return dominadas === 1
        ? 'dominando 1 pílula de conhecimento da Lei 14.133/2021.'
        : `dominando ${dominadas} pílulas de conhecimento da Lei 14.133/2021.`
}

// O PNG do wordmark vira data URI uma única vez por sessão. Falha (offline,
// asset movido) devolve null: o certificado sai sem a marca em vez de não
// sair — é o rodapé, não o conteúdo.
let wordmarkEmCache: Promise<string | null> | null = null

async function carregarWordmark(): Promise<string | null> {
    if (!wordmarkEmCache) {
        wordmarkEmCache = (async () => {
            const resposta = await fetch(wordmarkUrl)
            const blob = await resposta.blob()
            return await new Promise<string>((resolve, reject) => {
                const leitor = new FileReader()
                leitor.onload = () => resolve(String(leitor.result))
                leitor.onerror = () => reject(leitor.error)
                leitor.readAsDataURL(blob)
            })
        })().catch(() => {
            // Falha transitória (offline, asset momentaneamente fora) não
            // pode ficar cacheada a sessão inteira — todo certificado sairia
            // sem a marca. Limpa o cache para a próxima emissão refazer o
            // fetch; só o sucesso continua cacheado.
            wordmarkEmCache = null
            return null
        })
    }
    return wordmarkEmCache
}

function centralizado(
    doc: jsPDF,
    texto: string,
    y: number,
    opcoes: {charSpace?: number} = {}
) {
    doc.text(texto, LARGURA / 2, y, {align: 'center', ...opcoes})
}

export async function montarCertificado(
    dados: DadosCertificado
): Promise<jsPDF> {
    // Import dinâmico: o jspdf pesa centenas de KB e só interessa a quem
    // concluiu uma trilha inteira e pediu o certificado — não pode entrar no
    // chunk da trilha, que todo mundo baixa.
    const {jsPDF: JsPDF} = await import('jspdf')
    const doc = new JsPDF({unit: 'mm', format: 'a4', orientation: 'landscape'})
    const nome = dados.nome.trim()
    const emitidoEm = dados.emitidoEm ?? new Date()

    // moldura: traço grosso da marca por fora, fio fino por dentro
    doc.setDrawColor(COLORS.primariaEscura)
    doc.setLineWidth(1.4)
    doc.rect(10, 10, LARGURA - 20, ALTURA - 20)
    doc.setDrawColor(COR_LINHA)
    doc.setLineWidth(0.3)
    doc.rect(14, 14, LARGURA - 28, ALTURA - 28)

    doc.setTextColor(COR_APAGADA)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    centralizado(doc, 'LICITO.GURU · TRILHA DO GURU', 34, {charSpace: 1.2})

    doc.setTextColor(COLORS.primariaEscura)
    doc.setFontSize(30)
    centralizado(doc, 'CERTIFICADO DE CONCLUSÃO', 54)

    doc.setFillColor(COLORS.primariaEscura)
    doc.rect(LARGURA / 2 - 22, 60, 44, 1.2, 'F')

    doc.setTextColor(COR_APAGADA)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(13)
    centralizado(doc, 'Certificamos que', 76)

    if (nome) {
        doc.setTextColor(COR_TEXTO)
        doc.setFont('helvetica', 'bold')
        // Nome longo não pode vazar da moldura NEM ser cortado: quebrar em
        // duas linhas colidiria com o texto fixo logo abaixo, então o corpo
        // encolhe até o nome inteiro caber numa linha. Nome civil brasileiro
        // completo cabe muito antes do piso de 12pt; cortar o nome de alguém
        // no próprio certificado dele não é opção.
        const largura = LARGURA - 90
        let corpo = 24
        doc.setFontSize(corpo)
        while (corpo > 12 && doc.getTextWidth(nome) > largura) {
            corpo -= 1
            doc.setFontSize(corpo)
        }
        centralizado(doc, nome, 92)
    } else {
        // Sem nome no contexto de auth: linha para preencher à mão. Nada de
        // inventar nome a partir de e-mail ou login.
        doc.setDrawColor(COR_LINHA)
        doc.setLineWidth(0.4)
        doc.line(LARGURA / 2 - 75, 93, LARGURA / 2 + 75, 93)
        doc.setTextColor(COR_APAGADA)
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        centralizado(doc, 'nome de quem concluiu', 99)
    }

    doc.setTextColor(COR_APAGADA)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(13)
    centralizado(doc, 'concluiu integralmente a trilha', 110)

    doc.setTextColor(COR_TEXTO)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(20)
    const trilhaEmLinhas: string[] = doc.splitTextToSize(
        dados.trilha,
        LARGURA - 90
    )
    trilhaEmLinhas.slice(0, 2).forEach((linha, i) => {
        centralizado(doc, linha, 124 + i * 9)
    })

    doc.setTextColor(COR_TEXTO)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(13)
    centralizado(
        doc,
        frasePilulas(dados.dominadas),
        124 + Math.min(trilhaEmLinhas.length, 2) * 9 + 6
    )

    doc.setTextColor(COR_APAGADA)
    doc.setFontSize(11)
    centralizado(doc, `Emitido em ${dataDeEmissao(emitidoEm)}`, 162)

    const wordmark = await carregarWordmark()
    if (wordmark) {
        const altura = 9
        doc.addImage(
            wordmark,
            'PNG',
            26,
            174,
            altura * PROPORCAO_WORDMARK,
            altura
        )
    } else {
        // sem a imagem, o wordmark vira texto — o rodapé nunca fica órfão
        doc.setTextColor(COLORS.primariaEscura)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(13)
        doc.text('licito.guru', 26, 181)
    }

    doc.setTextColor(COR_APAGADA)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.text(
        'Documento interno de capacitação · Trilha do Guru',
        LARGURA - 26,
        181,
        {align: 'right'}
    )

    return doc
}

export async function baixarCertificado(
    dados: DadosCertificado
): Promise<void> {
    const doc = await montarCertificado(dados)
    doc.save(nomeDoArquivo(dados.trilha))
}
